import { useState } from "react";
import { FirebaseError } from "firebase/app";
import { PiEnvelopeSimple } from "react-icons/pi";
import {
  reloadEmailVerified,
  sendVerificationEmail,
  useUnverifiedEmail,
} from "./firebase";
import { lstr } from "./localization";

// EmailVerificationNotice asks a signed-in user whose email isn't verified to
// verify it, which raises their daily limits. It blocks nothing and renders
// nothing for anyone else.
export function EmailVerificationNotice({
  l,
  className = "",
}: {
  l: string;
  className?: string;
}) {
  const strings = lstr(l);
  const unverifiedEmail = useUnverifiedEmail();
  // True while a send or a check is in flight.
  const [pending, setPending] = useState(false);
  // What came of the last send or check.
  const [feedback, setFeedback] = useState<string | null>(null);

  if (!unverifiedEmail) return null;

  const runAndReport = async (action: () => Promise<string | null>) => {
    if (pending) return;
    setPending(true);
    setFeedback(null);
    try {
      setFeedback(await action());
    } catch (err) {
      console.error("Email verification action failed", err);
      setFeedback(
        err instanceof FirebaseError && err.code === "auth/too-many-requests"
          ? strings.login_error_too_many_requests
          : strings.email_verification_error,
      );
    } finally {
      setPending(false);
    }
  };

  // Also the first email when the one at sign-up failed (see LoginPage), so
  // the notice never claims an email was sent.
  const send = () =>
    runAndReport(async () => {
      await sendVerificationEmail(l);
      return strings.email_verification_sent;
    });

  // Once verified, the notice disappears (see useUnverifiedEmail).
  const check = () =>
    runAndReport(async () =>
      (await reloadEmailVerified())
        ? null
        : strings.email_verification_still_unverified,
    );

  const actionClassName =
    "font-semibold text-primary transition-colors hover:text-primary-hover disabled:opacity-50";

  return (
    <div className={`card flex gap-3 px-4 py-3 text-sm ${className}`}>
      <PiEnvelopeSimple
        size={20}
        aria-hidden
        className="mt-px shrink-0 text-primary"
      />
      <div className="min-w-0 flex-grow">
        <p className="break-words text-main-text">
          {strings.email_verification_notice.replace(
            "{email}",
            unverifiedEmail,
          )}
        </p>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
          <button
            type="button"
            onClick={send}
            disabled={pending}
            className={actionClassName}
          >
            {strings.email_verification_send}
          </button>
          <button
            type="button"
            onClick={check}
            disabled={pending}
            className={actionClassName}
          >
            {strings.email_verification_check}
          </button>
        </div>
        <p
          aria-live="polite"
          className="mt-1.5 text-secondary-text empty:hidden"
        >
          {feedback}
        </p>
      </div>
    </div>
  );
}
